<template>
  <div class="prod-page">
    <div v-if="error" class="move-alert-error">{{ error }}</div>
    <div v-else-if="loading" class="flex justify-center py-16">
      <div class="move-spinner" aria-label="Loading production" />
    </div>
    <template v-else-if="production">
      <section class="prod-hero">
        <div class="prod-hero__glow" />
        <div class="relative space-y-4">
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div class="min-w-0">
              <p class="prod-kicker">{{ production.project?.name }}</p>
              <div class="flex flex-wrap items-center gap-3">
                <h2 class="prod-hero__title">{{ production.name }}</h2>
                <span :class="statusBadgeClass(production.status)">{{ productionStatusLabels[production.status] || production.status }}</span>
              </div>
            </div>
          </div>
          <ProductionProgress
            :status="production.status"
            interactive
            @select="updateStatus"
          />
          <div v-if="heroMeta.length" class="prod-hero__meta">
            <span v-for="item in heroMeta" :key="item.label">
              <span class="material-icons">{{ item.icon }}</span>
              {{ item.label }}
            </span>
          </div>
          <div v-if="publishedLinks.length" class="flex flex-wrap gap-2">
            <a
              v-for="item in publishedLinks"
              :key="item.key"
              :href="item.url"
              target="_blank"
              rel="noopener"
              class="prod-link-chip"
            >
              {{ item.label }}
              <span class="material-icons">open_in_new</span>
            </a>
          </div>
          <ProductionGaps
            :gaps="production.gaps"
            :production-id="productionId"
            linked
            show-ready
          />
          <nav class="prod-subnav" aria-label="Production sections">
            <router-link
              v-for="item in productionContextNav"
              :key="item.name"
              v-slot="{ href, navigate, isActive, isExactActive }"
              :to="`/production/productions/${productionId}${item.suffix}`"
              custom
            >
              <a
                :href="href"
                class="prod-subnav__item"
                :class="{ 'is-active': item.suffix === '' ? isExactActive : isActive }"
                @click="navigate"
              >
                <span class="material-icons">{{ item.icon }}</span>
                {{ item.label }}
              </a>
            </router-link>
          </nav>
        </div>
      </section>
      <router-view />
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, provide, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import ProductionProgress from '../../components/production/ProductionProgress.vue'
import ProductionGaps from '../../components/production/ProductionGaps.vue'
import {
  platformLabels,
  productionContextNav,
  productionStatusLabels,
  statusBadgeClass
} from '../../config/productionNavigation'
import { productionApi } from '../../services/productionApi'
import { formatDateTime } from '../../utils/productionDates'

const route = useRoute()
const loading = ref(true)
const error = ref('')
const saving = ref(false)
const production = ref(null)

const productionId = computed(() => route.params.productionId)
const publishedLinks = computed(() => Object.entries(production.value?.release_links || {})
  .filter(([, url]) => url)
  .map(([key, url]) => ({
    key,
    url,
    label: platformLabels[key] || key
  })))

const heroMeta = computed(() => {
  const items = []
  if (production.value?.shoot_at) {
    items.push({ icon: 'event', label: formatDateTime(production.value.shoot_at) })
  }
  if (production.value?.shoot_venue) {
    items.push({ icon: 'place', label: production.value.shoot_venue })
  }
  if ((production.value?.platforms || []).length) {
    items.push({
      icon: 'devices',
      label: production.value.platforms.map((key) => platformLabels[key] || key).join(' · ')
    })
  }
  return items
})

async function reload () {
  const body = await productionApi.showProduction(productionId.value)
  production.value = body.data
}

async function updateStatus (status) {
  await save({ status })
}

async function save (payload) {
  saving.value = true
  error.value = ''
  try {
    const body = await productionApi.updateProduction(productionId.value, payload)
    production.value = body.data
    return body.data
  } catch (e) {
    error.value = e.response?.data?.message || Object.values(e.response?.data?.errors || {}).flat().join(' ') || 'Save failed.'
    throw e
  } finally {
    saving.value = false
  }
}

provide('productionWorkspace', {
  production,
  productionId,
  saving,
  error,
  reload,
  save
})

async function load () {
  loading.value = true
  error.value = ''
  try {
    await reload()
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to load production.'
  } finally {
    loading.value = false
  }
}

onMounted(load)
watch(productionId, load)
</script>
